import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import _ from "lodash";
import { logoutAndRedirectToLogin, reusableFetch } from "src/utils/useAxios";

function headers(token) {
  if (token) {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": token,
    };
  }

  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export const getUser = createAsyncThunk(
  "user/getUser",
  async ({}, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user`,
        method: "GET",
        headers: headers(token),
      }).catch((err) => {});
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

// Every user for master users, only their own organisation's for admins, a 403 for anyone else.
// reusableFetch sends the stored token and logs the user out on a 401
export const getAllUsers = createAsyncThunk(
  "user/getAllUsers",
  async (_arg, { rejectWithValue }) =>
    reusableFetch("user/all", "GET", null, rejectWithValue)
);

// Master users change another user's status (approve or deactivate them) or email status from the users page. Both
// return the user as that page lists them
const updateListedUser = (typePrefix, path) =>
  createAsyncThunk(typePrefix, async ({ userId, status }, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user/${userId}/${path}`,
        method: "POST",
        headers: headers(localStorage.token),
        data: { status },
      });
      // Only the body: the full axios response isn't serializable
      return { data: response.data };
    } catch (e) {
      // An expired session, handled as reusableFetch does
      if (e.response?.status === 401) logoutAndRedirectToLogin();
      return rejectWithValue(e.response?.data);
    }
  });

export const updateUserStatus = updateListedUser("user/updateStatus", "status");
export const updateUserEmailStatus = updateListedUser("user/updateEmailStatus", "email-status");

// Puts the user the server returned in place of the listed one
const replaceListedUser = (state, { payload }) => {
  state.users = state.users.map((user) => (user._id === payload.data._id ? payload.data : user));
};

// Registers the user with their organisation. No session comes back: the account can't be used until
// the email is verified and GGF approves the registration
export const registerUser = createAsyncThunk(
  "user/register",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user/register/`,
        method: "POST",
        headers: headers(),
        data,
      });
      // Only the body: the full axios response isn't serializable
      return { data: response.data };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/update",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user`,
        method: "POST",
        headers: headers(token),
        data,
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/login",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user/login/`,
        method: "POST",
        headers: headers(),
        data,
      });
      // Only the body: the full axios response isn't serializable
      return { data: response.data };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

// Second factor steps after loginUser. Plain axios (not reusableFetch) so a 401 for an
// expired sign-in returns to the login form instead of logging out.
const loginStep = (path) =>
  createAsyncThunk(`user/login/${path}`, async (data, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user/login/${path}`,
        method: "POST",
        headers: headers(),
        data,
      });

      return { data: response.data };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  });

export const verifyLoginCode = loginStep("verify");
export const resendLoginCode = loginStep("resend");
export const sendLoginMobileCode = loginStep("mobile");

export const forgotPassword = createAsyncThunk(
  "user/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/forget_password/`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({
            email,
          }),
        }
      );
      const userResponse = await response.json();
      if (userResponse.status === "error") {
        return rejectWithValue(userResponse);
      }
      return userResponse;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const updatePassword = createAsyncThunk(
  "user/updatePassword",
  async ({ email, password }, { rejectWithValue, getState }) => {
    try {
      const { userDetails } = getState();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user-credentials/password/update`,
        {
          method: "POST",
          headers: headers(userDetails.token),
          data: {
            email,
            password,
          },
        }
      );
      const userResponse = await response.json();
      if (userResponse.status === "error") {
        return rejectWithValue(userResponse);
      }
      return userResponse;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

// reusableFetch sends the stored token and logs the user out on a 401
export const validateToken = createAsyncThunk(
  "user/validate",
  async (_arg, { rejectWithValue }) =>
    reusableFetch("user/validate", "GET", null, rejectWithValue)
);

export const validateEmail = createAsyncThunk(
  "user/validateEmail",
  async (email, { rejectWithValue, getState }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user/email/validate/${email}`,
        method: "GET",
        headers: headers(),
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const requestResetPassword = createAsyncThunk(
  "reset-password/request",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user-credentials/reset-password/request/${encodeURIComponent(email)}`,
        method: "GET",
        headers: headers(),
      });

      return { data: response.data };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

// Sets the new password and starts the SMS second factor, answered like loginUser
export const resetPassword = createAsyncThunk(
  "reset-password/reset",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user-credentials/reset-password/reset`,
        method: "POST",
        headers: headers(),
        data,
      });

      return { data: response.data };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const updatePhoneNumber = createAsyncThunk(
  "user/updatePhoneNumber",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user/phone-number`,
        method: "POST",
        headers: headers(token),
        data,
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const changePassword = createAsyncThunk(
  "user/changePassword",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/user-credentials/change`,
        method: "POST",
        headers: headers(token),
        data,
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const updateSettings = createAsyncThunk(
  "user/settings",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/settings`,
        method: "POST",
        headers: headers(token),
        data,
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const getAllTransactions = createAsyncThunk(
  "user/getAllTransactions",
  async ({}, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/transactions/all`,
        method: "GET",
        headers: headers(token),
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const createSubscription = createAsyncThunk(
  "user/createSubscription",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/billing/subscription`,
        method: "POST",
        headers: headers(token),
        data,
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const getCustomerBilling = createAsyncThunk(
  "user/getCustomerBilling",
  async (data, { rejectWithValue }) => {
    const token = localStorage.token;
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API_URL}/billing/customer`,
        method: "GET",
        headers: headers(token),
      });
      if (response.status === "error") {
        return rejectWithValue(response);
      }
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

const initialState = {
  emailInUse: false,
  isCreatingAccount: false,
  changePasswordFail: null,
  isChangingPassword: null,
  changePasswordSuccess: false,
  templateSaveSuccess: false,
  transactions: null,
  clientSecret: null,
  isCreatingSubscription: false,
  users: [],
  gettingAllUsers: false,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    saveValue: (state, action) => {
      return _.merge(state, action.payload);
    },
    resetState: () => initialState,
  },
  extraReducers: {
    [getUser.fulfilled]: (state, { payload }) => {
      state = _.merge(state, payload.data);
    },
    [getUser.pending]: (state) => {
      state.isUpdating = true;
    },
    [getUser.rejected]: (state) => {
      state.isCreatingAccount = false;
    },
    [getAllUsers.pending]: (state) => {
      state.gettingAllUsers = true;
    },
    [getAllUsers.fulfilled]: (state, { payload }) => {
      state.users = payload.data;
      state.gettingAllUsers = false;
    },
    [getAllUsers.rejected]: (state) => {
      state.gettingAllUsers = false;
    },
    [updateUserStatus.fulfilled]: replaceListedUser,
    [updateUserEmailStatus.fulfilled]: replaceListedUser,
    [updateUser.fulfilled]: (state, { payload }) => {
      state = _.merge(state, payload.data);
      state.isUpdating = false;
      state.updateSuccess = true;
    },
    [updateUser.pending]: (state) => {
      state.isUpdating = true;
    },
    [updateUser.rejected]: (state, { payload }) => {
      state.isCreatingAccount = false;
    },

    [registerUser.fulfilled]: (state) => {
      state.isCreatingAccount = false;
    },
    [registerUser.pending]: (state) => {
      state.isCreatingAccount = true;
    },
    [registerUser.rejected]: (state) => {
      state.isCreatingAccount = false;
    },

    [loginUser.fulfilled]: (state, { payload }) => {
      state.errorMessage = null;
      // A normal login returns the SMS code step instead; the session comes from verifyLoginCode
      if (payload.data.token) {
        localStorage.token = payload.data.token;
        state = _.merge(state, payload.data.user);
      }
    },
    [verifyLoginCode.fulfilled]: (state, { payload }) => {
      localStorage.token = payload.data.token;
      state = _.merge(state, payload.data.user);
    },
    [loginUser.pending]: (state) => {
      state.isFetching = true;
    },
    [loginUser.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.message;
    },

    [forgotPassword.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.isFetching = false;
      state.isSuccess = true;
      return state;
    },
    [forgotPassword.pending]: (state) => {
      state.isFetching = true;
    },
    [forgotPassword.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [updatePassword.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.email = payload.email;
      state.successMessage = payload.message;
    },
    [updatePassword.pending]: (state) => {
      state.isFetching = true;
    },
    [updatePassword.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [updatePhoneNumber.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.successMessage = payload.message;
      state = _.merge(state, payload.data);
    },
    [updatePhoneNumber.pending]: (state) => {
      state.isFetching = true;
    },
    [updatePhoneNumber.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [validateToken.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state = _.merge(state, payload.data);
    },
    [validateToken.pending]: (state) => {
      state.isFetching = true;
    },
    [validateToken.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [validateEmail.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.emailInUse = payload.data.emailInUse;
    },
    [validateEmail.pending]: (state) => {
      state.isFetching = true;
    },
    [validateEmail.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [requestResetPassword.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
    },
    [requestResetPassword.pending]: (state) => {
      state.isFetching = true;
    },
    [requestResetPassword.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [resetPassword.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      // The reset now returns the SMS code step; the session comes from verifyLoginCode
      if (payload.data.token) {
        localStorage.token = payload.data.token;
        state = _.merge(state, payload.data.user);
      }
    },
    [resetPassword.pending]: (state) => {
      state.isFetching = true;
    },
    [resetPassword.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [changePassword.fulfilled]: (state, { payload }) => {
      localStorage.token = payload.data.token;
      state.isChangingPassword = false;
      state.changePasswordSuccess = true;
      state = _.merge(state, payload.data.user);
    },
    [changePassword.pending]: (state) => {
      state.isChangingPassword = true;
      state.changePasswordFail = true;
    },
    [changePassword.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isChangingPassword = false;
      state.changePasswordFail = true;
    },

    [updateSettings.fulfilled]: (state, { payload }) => {
      localStorage.token = payload.data.token;
      state.isChangingPassword = false;
      state.changePasswordSuccess = true;
      state = _.merge(state, payload.data);
      state.templateSaveSuccess = true;
    },
    [updateSettings.pending]: (state) => {
      state.isChangingPassword = true;
      state.changePasswordFail = true;
    },
    [updateSettings.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isChangingPassword = false;
      state.changePasswordFail = true;
    },

    [getAllTransactions.fulfilled]: (state, { payload }) => {
      state = _.merge(state.transactions, payload.data);
    },
    [getAllTransactions.pending]: (state) => {
      state.isChangingPassword = true;
    },
    [getAllTransactions.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
    },

    [createSubscription.fulfilled]: (state, { payload }) => {
      state = _.merge(state, payload.data);
      state.isCreatingSubscription = false;
    },
    [createSubscription.pending]: (state) => {
      state.isChangingPassword = true;
      state.isCreatingSubscription = true;
    },
    [createSubscription.rejected]: (state, { payload }) => {
      state.isCreatingSubscription = false;
    },

    [getCustomerBilling.fulfilled]: (state, { payload }) => {
      state.isCreatingSubscription = false;
      state = _.merge(state, payload.data);
    },
    [getCustomerBilling.pending]: (state) => {
      state.isChangingPassword = true;
      state.isCreatingSubscription = true;
    },
    [getCustomerBilling.rejected]: (state, { payload }) => {
      state.isCreatingSubscription = false;
    },
  },
});

export const { saveValue, resetState } = userSlice.actions;
export const userSelector = (state) => state.user;
